import { api } from "./client";
import type { ApiResponse } from "./types";

// ============================================================================
// Telemetry: the five writes, and the four reads that add them up.
//
// Every write is FIRE-AND-FORGET and takes NO auth — `security: []` in the
// spec, `optionalAuth` on the route. That is deliberate: a page view from
// somebody not signed in is still a page view, and the most interesting
// searches come from people who have not joined yet. A client that only
// reported telemetry for signed-in users would miss exactly the population
// worth learning from.
//
// The reads split three ways: your own profile, a business you own, and two
// that are ADMIN ONLY.
//
// ⚠️ THE WRITES DO NOT USE THE ENVELOPE. Every other endpoint in this API
// answers `{ success, data }`. These five answer `{ success, id }` with the id
// at the TOP LEVEL and no `data` at all. It is the documented shape, and it is
// the one exception in the whole API — reading `response.data.id` here gets
// undefined, which is exactly how this was got wrong the first time.
// ============================================================================

/** The write reply: no `data` wrapper. `id` is null when the write was swallowed. */
export interface TrackResult {
  success: true;
  /** Null on a swallowed write — not an error. Telemetry never fails a page. */
  id: string | null;
}

export const ANALYTICS_EVENT_TYPES = [
  "page_view", "feature_use", "search", "profile_view", "provider_view",
  "event_view", "question_view", "button_click", "form_submit", "share",
  "download", "funnel_step",
] as const;

/** Fire-and-forget: `id` comes back null when the write was swallowed. */
export function trackEvent(input: {
  eventType: string;
  eventName: string;
  referenceType?: string;
  referenceId?: string;
  pageUrl?: string;
  metadata?: Record<string, unknown>;
}) {
  return api.post<TrackResult>("/analytics/events", input);
}

export function trackProfileView(profileUserId: string) {
  return api.post<TrackResult>("/analytics/profile-views", {
    profileUserId,
  });
}

export const PROVIDER_VIEW_SOURCES = ["search", "direct", "question", "recommendation"] as const;

/**
 * The write the shop dashboard is waiting on.
 *
 * `profileViewsTracked` on the owner dashboard is false precisely because
 * nothing calls this yet — which is why that dashboard HIDES its view count
 * rather than showing a structural zero. Call this and the metric becomes real.
 */
export function trackProviderView(providerId: string, source?: string) {
  return api.post<TrackResult>("/analytics/provider-views", {
    providerId,
    ...(source ? { source } : {}),
  });
}

/**
 * A search that happened, and how many results it found.
 *
 * `resultCount: 0` is the important case, not the boring one. It is the only
 * record that somebody looked for something WeDeshi could not show them, and
 * it is what `zeroResultQueries` below is built from.
 */
export function trackSearchQuery(input: {
  query: string;
  resultCount: number;
  category?: string;
  filters?: Record<string, unknown>;
}) {
  return api.post<TrackResult>("/analytics/search-queries", input);
}

/**
 * Which result they actually took, and where it sat in the list.
 *
 * `clickPosition` is what turns searches into a ranking signal: a result
 * clicked at position nine every time is a ranking that is wrong.
 */
export function trackSearchClick(input: {
  searchQueryId: string;
  clickedType: string;
  clickedId: string;
  clickPosition: number;
}) {
  return api.post<TrackResult>("/analytics/search-clicks", input);
}

export interface Period {
  start: string;
  end: string;
}

export interface ProfileAnalytics {
  period: Period;
  metrics: {
    totalViews: number;
    viewsTimeline: { date: string; views: number }[];
    /** `user` is nullable — an anonymous viewer is counted, never named. */
    topViewers: { user: { id: string; name: string; city: string | null } | null; viewCount: number }[];
  };
}

export function getMyProfileAnalytics(params?: { period?: string }) {
  return api.get<ApiResponse<ProfileAnalytics>>(
    "/analytics/me/profile",
    params as Record<string, string | number | boolean | undefined>,
  );
}

export interface ProviderAnalytics {
  provider: { id: string; name: string; slug: string };
  period: Period;
  metrics: {
    totalViews: number;
    viewsTimeline: { date: string; views: number }[];
    vouches: { total: number; periodNew: number; avgRating: number; periodAvgRating: number | null };
    mentions: { total: number; periodNew: number };
    searchClicks: number;
  };
}

export function getProviderAnalytics(id: string, params?: { period?: string }) {
  return api.get<ApiResponse<ProviderAnalytics>>(
    `/analytics/providers/${id}`,
    params as Record<string, string | number | boolean | undefined>,
  );
}

export interface PlatformAnalytics {
  period: Period;
  overview: { totalEvents: number; activeUsers: number };
  eventsByType: { type: string; count: number }[];
  eventsTrend: { date: string; count: number }[];
  topSearches: { query: string; count: number }[];
  /** `device` is nullable — unknown is a real bucket, not a gap. */
  deviceBreakdown: { device: string | null; count: number }[];
}

/** ADMIN ONLY. */
export function getPlatformAnalytics(params?: { period?: string }) {
  return api.get<ApiResponse<PlatformAnalytics>>(
    "/analytics/platform",
    params as Record<string, string | number | boolean | undefined>,
  );
}

export interface SearchAnalytics {
  period: Period;
  overview: { totalSearches: number; avgResultCount: number; clickThroughRate: number };
  byCategory: { category: string; count: number }[];
  topQueries: { query: string; count: number; avgResults: number }[];
  /**
   * What people looked for and did not find.
   *
   * This is the most valuable list in the analytics module and the least
   * obvious. Everything else measures what WeDeshi already does; this measures
   * what it was asked for and could not do — the demand that leaves no other
   * trace. It is the evidence base for the search-and-discovery pillar, and
   * for knowing which businesses to go and recruit.
   */
  zeroResultQueries: { query: string; count: number }[];
  /**
   * Individual words people searched for that appear in NO document.
   *
   * The list `zeroResultQueries` cannot produce. A search for a trade nobody
   * offers returns near neighbours rather than nothing, so it never shows up
   * as a zero — but the WORD went unanswered, and that names who to recruit.
   */
  unansweredTerms: { term: string; count: number }[];
}

/** ADMIN ONLY. */
export function getSearchAnalytics(params?: { period?: string }) {
  return api.get<ApiResponse<SearchAnalytics>>(
    "/analytics/search",
    params as Record<string, string | number | boolean | undefined>,
  );
}
